import { formatBillingAuditActor, formatRetentionAuditDetails } from "@/lib/billingAuditPresentation";
import { useState, type ChangeEvent } from "react";
import { trpc } from "@/lib/trpc";
import { calculateInvoiceVatAmount, type BillingVatRate } from "@shared/billing/invoice-vat";
import { BillingVatRateSelect } from "../billing/BillingVatRateSelect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  ArrowLeftRight,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  History,
  Info,
  MoreVertical,
  Pencil,
  Upload,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL,
  isInvoicePaidPendingEconomicReview,
} from "@/finance/billingCollectionPresentation";

// Display only: never feed localized values back into inputs or mutations.
function moneyDisplay(value: string | number | null | undefined, currency: string): string {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("es-ES", { style: "currency", currency })
    : "—";
}

function dateDisplay(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("es-ES", { timeZone: "UTC" });
}

export interface ContractMilestoneItem {
  id: number;
  label: string;
  type: string;
  status: string;
  percentage: string | null;
  fixedAmount: string | null;
  calculatedAmount: string | null;
  dueDate: string | null;
  certificationId?: number | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | Date | null;
  invoiceAmount?: string | null;
  invoiceTaxableBase?: string | null;
  invoiceTaxAmount?: string | null;
  invoiceWithholdingAmount?: string | null;
  approvedTaxableBase?: string | null;
  approvedVatAmount?: string | null;
  approvedWithholdingAmount?: string | null;
  approvedPayable?: string | null;
  certificationEconomicReviewRequired?: boolean;
  certificationEconomicReviewMessage?: string | null;
  invoicePdfUrl?: string | null;
  paymentDate?: string | Date | null;
  paymentAmount?: string | null;
  paymentMethod?: string | null;
  effectivePaymentMethod?: string | null;
  bankCommissionAmount?: string | null;
  bankInterestAmount?: string | null;
  financialCostAmount?: string | null;
  netReceivedAmount?: string | null;
  notes?: string | null;
  collectedAmount?: string | null;
  balanceAmount?: string | null;
  invoiceFiscalVerified?: boolean;
  invoiceEconomicReviewRequired?: boolean;
  invoiceEconomicReviewMessage?: string | null;
  collectionEvents?: Array<Record<string, unknown>>;
}

type InvoiceFileData = {
  name: string;
  data: string;
  mime: "application/pdf";
};

type RevertTargetStatus =
  | "sent_to_client"
  | "pending"
  | "ready_to_invoice"
  | "accounting_notified"
  | "invoiced";

type AuditDetails = {
  reason?: unknown;
  cancellationReason?: unknown;
  fromStatus?: unknown;
  toStatus?: unknown;
  invoiceNumber?: unknown;
  invoiceAmount?: unknown;
  paymentAmount?: unknown;
};

interface BillingMilestoneAuditLog {
  id: number;
  action: string;
  createdAt: string | Date;
  userId: number | null;
  userName?: string | null;
  details: string | null;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function parseSettlementMoney(value: string): number | null {
  const raw = value.trim();
  if (!raw) return null;
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function getApprovedMilestoneAmount(milestone: ContractMilestoneItem): string | null {
  // Match the economic resolver's type-specific precedence. A linked accepted
  // legacy certification milestones use netCertifiedAmount; V1 uses frozen revision payable; percentage
  // milestones use their validated calculatedAmount, never the base.
  if (milestone.type === "percentage") return milestone.calculatedAmount;
  if (milestone.type === "monthly_certification") return milestone.calculatedAmount;
  if (milestone.type === "fixed_amount") return milestone.fixedAmount;
  return null;
}

function isPdfFile(file: File): boolean {
  return (file.type === "" || file.type === "application/pdf") && /\.pdf$/i.test(file.name);
}

function parseAuditDetails(value: unknown): AuditDetails | null {
  if (typeof value !== "string") return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed as AuditDetails : null;
  } catch {
    return null;
  }
}

interface Props {
  milestone: ContractMilestoneItem;
  currency?: string;
  userRole?: string;
  contextLabel?: string;
  onMutated?: (milestoneId?: number) => void;
  onEdit?: () => void;
  isHistorical?: boolean;
}

export function canPerformOperationalAction(role?: string | null): boolean {
  if (!role) return false;
  return ["admin", "admin_manager", "project_manager"].includes(role);
}

export function canPerformFinanceAction(role?: string | null): boolean {
  if (!role) return false;
  return ["admin", "admin_manager"].includes(role);
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  retention_classified: "Hito clasificado como retención",
  retention_updated: "Retención actualizada",
  retention_due_observed: "Vencimiento de retención observado (aviso interno)",
  create: "Creado",
  update: "Actualizado",
  markReady: "Listo para facturar",
  accounting_notified: "Aviso a Contabilidad",
  notifyAccounting: "Aviso a Contabilidad",
  markInvoiced: "Facturado",
  markSentToClient: "Envío al cliente registrado",
  attachInvoicePdf: "PDF de factura adjuntado",
  markPendingCollection: "Pendiente de cobro",
  markCollected: "Cobrado",
  cancel: "Cancelado",
  revertStatus: "Estado revertido",
  repairLegacyBase: "Base histórica reparada",
};

const BILLING_STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  ready_to_invoice: "Listo para facturar",
  accounting_notified: "Contabilidad notificada",
  invoiced: "Facturado",
  sent_to_client: "Enviado al cliente",
  pending_collection: "Pendiente de cobro",
  collected: "Cobrado",
  cancelled: "Cancelado",
};

const REVERT_TARGET_LABELS: Record<RevertTargetStatus, string> = {
  sent_to_client: "Enviado al cliente",
  pending: "Pendiente",
  ready_to_invoice: "Listo para facturar",
  accounting_notified: "Contabilidad notificada",
  invoiced: "Facturado",
};

type QuickLifecycleActionKind = "markReady" | "notifyAccounting" | "markSentToClient" | "markPendingCollection";

const QUICK_LIFECYCLE_ACTIONS: Record<
  QuickLifecycleActionKind,
  { title: string; description: string; destinationLabel: string; confirmLabel: string }
> = {
  markReady: {
    title: "Marcar listo para facturar",
    description: "Confirma el cambio de estado del hito antes de aplicarlo.",
    destinationLabel: "Listo para facturar",
    confirmLabel: "Confirmar: listo para facturar",
  },
  notifyAccounting: {
    title: "Avisar a Contabilidad",
    description: "Se registrará el aviso a Contabilidad para este hito.",
    destinationLabel: "Contabilidad notificada",
    confirmLabel: "Confirmar aviso a Contabilidad",
  },
  markSentToClient: {
    title: "Registrar envío al cliente",
    description: "Se registrará manualmente que la factura emitida se ha enviado al cliente. No se enviará ningún correo desde la aplicación.",
    destinationLabel: "Enviado al cliente",
    confirmLabel: "Confirmar registro de envío",
  },
  markPendingCollection: {
    title: "Marcar pendiente de cobro",
    description: "El hito quedará a la espera de registrar el cobro.",
    destinationLabel: "Pendiente de cobro",
    confirmLabel: "Confirmar: pendiente de cobro",
  },
};

// Scope sizing to lifecycle dialogs: long customer/contract tokens must not
// determine the grid's intrinsic width. The whole dialog scrolls vertically,
// keeping header, body and footer in normal flow at short viewport heights.
const lifecycleDialogClassName =
  "w-[calc(100%-2rem)] max-w-md sm:max-w-md max-h-[90dvh] flex flex-col overflow-y-auto [overflow-wrap:anywhere] [&>*]:min-w-0 [&>*]:shrink-0";
const lifecycleSummaryRowClassName =
  "grid min-w-0 grid-cols-1 gap-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] sm:gap-3 [&>span]:min-w-0 [&>span]:[overflow-wrap:anywhere]";

// BILL-04 (P0-B): shared contextual summary for every confirmation step. Makes
// the mutation unambiguous (milestone, optional client/contract context, amount
// when available, current status → destination status) without fabricating data.
function MilestoneActionSummary({
  milestone,
  currency,
  contextLabel,
  destinationLabel,
  amountOverride,
}: {
  milestone: ContractMilestoneItem;
  currency: string;
  contextLabel?: string;
  destinationLabel: string;
  amountOverride?: string | null;
}) {
  const approvedAmount = milestone.certificationEconomicReviewRequired === true ? null : getApprovedMilestoneAmount(milestone);
  const currentStatusLabel = isInvoicePaidPendingEconomicReview(milestone)
    ? INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL
    : BILLING_STATUS_LABELS[milestone.status] ?? "Estado registrado";
  return (
    <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1" data-testid="milestone-action-summary">
      <div className={lifecycleSummaryRowClassName}>
        <span className="text-muted-foreground shrink-0">Hito:</span>
        <span className="font-medium text-left sm:text-right">{milestone.label}</span>
      </div>
      {contextLabel ? (
        <div className={lifecycleSummaryRowClassName}>
          <span className="text-muted-foreground shrink-0">Cliente / Contrato:</span>
          <span className="text-left sm:text-right">{contextLabel}</span>
        </div>
      ) : null}
      {approvedAmount != null ? (
        <div className={lifecycleSummaryRowClassName}>
          <span className="text-muted-foreground shrink-0">Importe aprobado:</span>
          <span className="text-left sm:text-right" data-testid="approved-amount">{moneyDisplay(approvedAmount, currency)}</span>
        </div>
      ) : null}
      {milestone.invoiceAmount != null ? (
        <div className={lifecycleSummaryRowClassName}>
          <span className="text-muted-foreground shrink-0">Factura exigible:</span>
          <span className="text-left sm:text-right">{moneyDisplay(milestone.invoiceAmount, currency)}</span>
        </div>
      ) : null}
      {amountOverride != null ? (
        <div className={lifecycleSummaryRowClassName}>
          <span className="text-muted-foreground shrink-0">Importe de esta operación:</span>
          <span className="text-left sm:text-right" data-testid="operation-amount">{moneyDisplay(amountOverride, currency)}</span>
        </div>
      ) : null}
      <div className={lifecycleSummaryRowClassName}>
        <span className="text-muted-foreground shrink-0">Estado actual:</span>
        <span className="text-left sm:text-right" data-testid="summary-current-status">
          {currentStatusLabel}
        </span>
      </div>
      <div className={lifecycleSummaryRowClassName}>
        <span className="text-muted-foreground shrink-0">Estado destino:</span>
        <span className="font-semibold text-left sm:text-right" data-testid="summary-destination-status">
          {destinationLabel}
        </span>
      </div>
    </div>
  );
}

export function ContractMilestoneLifecycleActions({
  milestone,
  currency = "EUR",
  userRole,
  contextLabel,
  onMutated,
  onEdit,
  isHistorical = false,
}: Props) {
  const { user } = useAuth();
  const effectiveRole = userRole ?? user?.role;
  const canOperational = canPerformOperationalAction(effectiveRole);
  const canFinance = canPerformFinanceAction(effectiveRole);
  const canReadLifecycle = canOperational || canFinance;

  const markReady = trpc.billingMilestones.markReady.useMutation();
  const notifyAccounting = trpc.billingMilestones.notifyAccounting.useMutation();
  const markInvoiced = trpc.billingMilestones.markInvoiced.useMutation();
  const attachInvoicePdf = trpc.billingMilestones.attachInvoicePdf.useMutation();
  const markSentToClient = trpc.billingMilestones.markSentToClient.useMutation();
  const markPendingCollection = trpc.billingMilestones.markPendingCollection.useMutation();
  const recordCollection = trpc.billingMilestones.recordCollection.useMutation();
  const cancelMilestone = trpc.billingMilestones.cancel.useMutation();
  const revertStatus = trpc.billingMilestones.revertStatus.useMutation();

  // Dialog open states
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  // BILL-04: quick lifecycle actions are never applied directly; they open a
  // confirmation dialog and mutate only after an explicit confirmation.
  const [quickAction, setQuickAction] = useState<QuickLifecycleActionKind | null>(null);
  const [quickActionConfirmed, setQuickActionConfirmed] = useState(false);

  // Queries
  const getByIdQuery = trpc.billingMilestones.getById.useQuery(
    { id: milestone.id },
    { enabled: detailOpen || invoiceOpen || paymentOpen }
  );

  const auditLogQuery = trpc.billingMilestones.auditLog.useQuery(
    { billingMilestoneId: milestone.id },
    { enabled: auditOpen }
  );

  // Form states
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceTaxableBase, setInvoiceTaxableBase] = useState("");
  const [invoiceTaxAmount, setInvoiceTaxAmount] = useState("");
  const [invoiceVatRate, setInvoiceVatRate] = useState<BillingVatRate>("manual");
  const [invoiceWithholdingAmount, setInvoiceWithholdingAmount] = useState("");
  const handleInvoiceBaseChange = (value: string) => {
    setInvoiceTaxableBase(value);
    if (invoiceVatRate !== "manual") {
      const calculated = calculateInvoiceVatAmount(value, invoiceVatRate);
      if (calculated !== null) setInvoiceTaxAmount(calculated);
    }
  };
  const handleInvoiceVatRateChange = (value: string) => {
    const rate = value as BillingVatRate;
    setInvoiceVatRate(rate);
    const calculated = calculateInvoiceVatAmount(invoiceTaxableBase, rate);
    if (calculated !== null) setInvoiceTaxAmount(calculated);
  };
  const [invoiceFileData, setInvoiceFileData] = useState<InvoiceFileData | null>(null);
  const [invoiceConfirmed, setInvoiceConfirmed] = useState(false);

  const [paymentDate, setPaymentDate] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("transfer");
  const [paymentReference, setPaymentReference] = useState("");
  const [collectionIdempotencyKey, setCollectionIdempotencyKey] = useState("");
  const [bankCommissionAmount, setBankCommissionAmount] = useState("0.00");
  const [bankInterestAmount, setBankInterestAmount] = useState("0.00");
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const [cancellationReason, setCancellationReason] = useState("");
  const [cancelConfirmed, setCancelConfirmed] = useState(false);

  const [targetStatus, setTargetStatus] = useState<RevertTargetStatus | "">("");
  const [revertReason, setRevertReason] = useState("");
  const [revertConfirmed, setRevertConfirmed] = useState(false);

  const [attachPdfFileData, setAttachPdfFileData] = useState<InvoiceFileData | null>(null);

  const [dialogError, setDialogError] = useState<string | null>(null);

  function handleFileRead(
    e: ChangeEvent<HTMLInputElement>,
    setter: (val: InvoiceFileData | null) => void,
  ) {
    const file = e.target.files?.[0];
    if (!file) {
      setter(null);
      return;
    }
    if (!isPdfFile(file)) {
      setter(null);
      setDialogError("Solo se pueden adjuntar archivos PDF.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const separator = result.indexOf(",");
      const base64 = separator >= 0 ? result.slice(separator + 1) : "";
      if (!base64) {
        setter(null);
        setDialogError("No se pudo leer el PDF seleccionado.");
        return;
      }
      setter({
        name: file.name,
        data: base64,
        mime: "application/pdf",
      });
      setDialogError(null);
    };
    reader.onerror = () => {
      setter(null);
      setDialogError("No se pudo leer el PDF seleccionado.");
    };
    reader.onabort = () => {
      setter(null);
      setDialogError("La lectura del PDF fue cancelada.");
    };
    try {
      reader.readAsDataURL(file);
    } catch {
      setter(null);
      setDialogError("No se pudo leer el PDF seleccionado.");
    }
  }

  // Action handlers
  function openQuickActionDialog(kind: QuickLifecycleActionKind) {
    setQuickActionConfirmed(false);
    setDialogError(null);
    setQuickAction(kind);
  }

  async function runQuickAction(kind: QuickLifecycleActionKind) {
    if (kind === "markReady") {
      await markReady.mutateAsync({ id: milestone.id });
      toast.success("Hito marcado como listo para facturar");
    } else if (kind === "notifyAccounting") {
      const milestoneDueDate = milestone.dueDate || getByIdQuery.data?.dueDate;
      if (!milestoneDueDate) {
        setDialogError("Para avisar a Contabilidad debes indicar primero la fecha prevista de facturación.");
        return;
      }
      const res = await notifyAccounting.mutateAsync({ id: milestone.id });
      if (res.noop) {
        toast.info("Aviso a Contabilidad ya registrado anteriormente.");
      } else {
        toast.success("Notificación a Contabilidad registrada.");
      }
    } else if (kind === "markSentToClient") {
      await markSentToClient.mutateAsync({ id: milestone.id });
      toast.success("Envío al cliente registrado (sin envío automático de correo)");
    } else {
      await markPendingCollection.mutateAsync({ id: milestone.id });
      toast.success("Hito marcado como pendiente de cobro");
    }
    setQuickAction(null);
    onMutated?.(milestone.id);
  }

  async function handleQuickActionConfirm() {
    if (!quickAction) return;
    if (!quickActionConfirmed) {
      setDialogError("Debes confirmar explícitamente este cambio de estado.");
      return;
    }
    setDialogError(null);
    try {
      await runQuickAction(quickAction);
    } catch (err: unknown) {
      setDialogError(errorMessage(err, "Error al aplicar el cambio de estado."));
    }
  }

  function openInvoiceDialog() {
    const details = getByIdQuery.data ?? milestone;
    const invalidCertificationSnapshot = details.certificationEconomicReviewRequired === true;
    const v1Certification = !invalidCertificationSnapshot && details.certificationId != null && details.approvedTaxableBase != null;
    const initialBase = details.invoiceTaxableBase ?? (invalidCertificationSnapshot ? null : v1Certification ? details.approvedTaxableBase : getApprovedMilestoneAmount(details));
    setInvoiceNumber(milestone.invoiceNumber ?? "");
    const initialDate = milestone.invoiceDate
      ? typeof milestone.invoiceDate === "string"
        ? milestone.invoiceDate.slice(0, 10)
        : milestone.invoiceDate.toISOString().slice(0, 10)
      : "";
    setInvoiceDate(initialDate);
    setInvoiceTaxableBase(initialBase != null ? String(initialBase) : "");
    setInvoiceTaxAmount(details.invoiceTaxAmount != null ? String(details.invoiceTaxAmount) : (invalidCertificationSnapshot ? "" : v1Certification ? String(details.approvedVatAmount ?? "0.00") : ""));
    setInvoiceVatRate("manual");
    setInvoiceWithholdingAmount(details.invoiceWithholdingAmount != null ? String(details.invoiceWithholdingAmount) : (invalidCertificationSnapshot ? "" : v1Certification ? String(details.approvedWithholdingAmount ?? "0.00") : ""));
    setInvoiceFileData(null);
    setInvoiceConfirmed(false);
    setDialogError(null);
    setInvoiceOpen(true);
  }

  async function handleMarkInvoicedSubmit() {
    if (!invoiceNumber.trim()) {
      setDialogError("Indica el número de factura.");
      return;
    }
    if (!invoiceDate.trim()) {
      setDialogError("Indica la fecha de la factura.");
      return;
    }
    const base = parseSettlementMoney(invoiceTaxableBase);
    const tax = parseSettlementMoney(invoiceTaxAmount);
    const withholding = parseSettlementMoney(invoiceWithholdingAmount);
    if (base === null || base <= 0 || tax === null || tax < 0 || withholding === null || withholding < 0) {
      setDialogError("Indica base imponible, IVA y retención válidos. Usa 0,00 cuando no corresponda.");
      return;
    }
    const payableCents = Math.round(base * 100) + Math.round(tax * 100) - Math.round(withholding * 100);
    if (payableCents <= 0) {
      setDialogError("El total exigible de factura debe ser positivo.");
      return;
    }
    if (!invoiceConfirmed) {
      setDialogError("Debes confirmar explícitamente el registro de la factura.");
      return;
    }
    setDialogError(null);
    try {
      await markInvoiced.mutateAsync({
        id: milestone.id,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate: new Date(invoiceDate),
        invoiceTaxableBase: String(base.toFixed(2)),
        invoiceTaxAmount: String(tax.toFixed(2)),
        invoiceWithholdingAmount: String(withholding.toFixed(2)),
        invoiceAmount: (payableCents / 100).toFixed(2),
        ...(invoiceFileData
          ? {
              invoiceFileName: invoiceFileData.name,
              invoiceFileData: invoiceFileData.data,
              invoiceFileMimeType: invoiceFileData.mime,
            }
          : {}),
      });
      toast.success("Factura registrada correctamente");
      setInvoiceOpen(false);
      onMutated?.(milestone.id);
    } catch (err: unknown) {
      setDialogError(errorMessage(err, "Error al registrar la factura."));
    }
  }

  function openPaymentDialog() {
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentAmount("");
    setPaymentMethod(getByIdQuery.data?.effectivePaymentMethod ?? milestone.effectivePaymentMethod ?? milestone.paymentMethod ?? "transfer");
    setPaymentReference("");
    setCollectionIdempotencyKey(crypto.randomUUID());
    setBankCommissionAmount("0.00");
    setBankInterestAmount("0.00");
    setPaymentConfirmed(false);
    setDialogError(null);
    setPaymentOpen(true);
  }

  async function handleMarkCollectedSubmit() {
    if (!paymentDate.trim()) {
      setDialogError("Indica la fecha de cobro.");
      return;
    }
    if (detailData.invoiceFiscalVerified !== true) {
      setDialogError("La factura no tiene un desglose fiscal verificable. Completa o revisa sus datos antes de registrar el cobro.");
      return;
    }
    const numAmount = parseSettlementMoney(paymentAmount);
    if (!paymentAmount.trim() || numAmount === null || !Number.isFinite(numAmount) || numAmount <= 0) {
      setDialogError("Indica un importe de cobro válido.");
      return;
    }
    const remaining = detailData.balanceAmount == null ? null : parseSettlementMoney(String(detailData.balanceAmount));
    if (remaining === null || remaining <= 0 || numAmount > remaining + 0.005) {
      setDialogError(remaining !== null && remaining <= 0
        ? "La factura ya está liquidada; no queda saldo para registrar otro cobro."
        : "El cobro supera el saldo de la factura. El excedente no se asignará automáticamente.");
      return;
    }
    const commission = bankCommissionAmount === "" ? 0 : parseSettlementMoney(bankCommissionAmount);
    const interest = bankInterestAmount === "" ? 0 : parseSettlementMoney(bankInterestAmount);
    if (paymentMethod === "confirming" &&
      (commission === null || interest === null || commission < 0 || interest < 0)) {
      setDialogError("La comisión y los intereses deben ser importes válidos no negativos.");
      return;
    }
    if (paymentMethod === "confirming" && commission! + interest! > numAmount) {
      setDialogError("La suma de comisión e intereses no puede superar el importe bruto satisfecho.");
      return;
    }
    if (!paymentConfirmed) {
      setDialogError("Debes confirmar explícitamente el registro del cobro.");
      return;
    }
    setDialogError(null);
    try {
      const result = await recordCollection.mutateAsync({
        billingMilestoneId: milestone.id,
        occurredAt: new Date(paymentDate + "T12:00:00"),
        amount: String(numAmount.toFixed(2)),
        paymentMethod: paymentMethod as "transfer" | "confirming" | "promissory_note" | "other" | "cash",
        reference: paymentReference.trim() || undefined,
        idempotencyKey: collectionIdempotencyKey,
        ...(paymentMethod === "confirming"
          ? {
              bankCommissionAmount: bankCommissionAmount || "0.00",
              bankInterestAmount: bankInterestAmount || "0.00",
            }
          : {}),
      });
      const remainingAfter = result.balanceAmount == null ? null : Number(result.balanceAmount);
      if (result.invoiceEconomicReviewRequired) {
        toast.warning("Cobro registrado; la diferencia entre plan y factura requiere revisión antes de cerrar el hito.");
      } else if (remainingAfter != null && remainingAfter > 0) {
        toast.success("Cobro parcial registrado; la factura conserva saldo pendiente.");
      } else {
        toast.success("Factura liquidada y hito cerrado.");
      }
      setPaymentOpen(false);
      onMutated?.(milestone.id);
    } catch (err: unknown) {
      setDialogError(errorMessage(err, "Error al registrar el cobro."));
    }
  }

  function openCancelDialog() {
    setCancellationReason("");
    setCancelConfirmed(false);
    setDialogError(null);
    setCancelOpen(true);
  }

  async function handleCancelSubmit() {
    const reason = cancellationReason.trim();
    if (reason.length < 5 || reason.length > 500) {
      setDialogError("El motivo de cancelación debe tener entre 5 y 500 caracteres.");
      return;
    }
    if (!cancelConfirmed) {
      setDialogError("Debes confirmar explícitamente la cancelación.");
      return;
    }
    setDialogError(null);
    try {
      await cancelMilestone.mutateAsync({
        id: milestone.id,
        cancellationReason: reason,
      });
      toast.success("Hito cancelado correctamente");
      setCancelOpen(false);
      onMutated?.(milestone.id);
    } catch (err: unknown) {
      setDialogError(errorMessage(err, "Error al cancelar el hito."));
    }
  }

  function openRevertDialog() {
    // Determine default target status
    let defStatus: RevertTargetStatus | "" = "";
    if (milestone.status === "ready_to_invoice") defStatus = "pending";
    else if (milestone.status === "accounting_notified") defStatus = "ready_to_invoice";
    else if (milestone.status === "invoiced") defStatus = "accounting_notified";
    else if (milestone.status === "pending_collection") defStatus = "sent_to_client";

    setTargetStatus(defStatus);
    setRevertReason("");
    setRevertConfirmed(false);
    setDialogError(null);
    setRevertOpen(true);
  }

  async function handleRevertSubmit() {
    if (!targetStatus) {
      setDialogError("Selecciona el estado al que deseas revertir.");
      return;
    }
    const reason = revertReason.trim();
    if (reason.length < 5 || reason.length > 500) {
      setDialogError("El motivo debe tener entre 5 y 500 caracteres.");
      return;
    }
    if (!revertConfirmed) {
      setDialogError("Debes confirmar explícitamente el cambio de estado.");
      return;
    }
    setDialogError(null);
    try {
      await revertStatus.mutateAsync({
        id: milestone.id,
        targetStatus,
        reason,
      });
      toast.success("Estado de facturación revertido correctamente");
      setRevertOpen(false);
      onMutated?.(milestone.id);
    } catch (err: unknown) {
      setDialogError(errorMessage(err, "Error al revertir el estado."));
    }
  }

  function openPdfDialog() {
    setAttachPdfFileData(null);
    setDialogError(null);
    setPdfOpen(true);
  }

  async function handleAttachPdfSubmit() {
    if (!attachPdfFileData) {
      setDialogError("Selecciona un archivo PDF de factura.");
      return;
    }
    setDialogError(null);
    try {
      await attachInvoicePdf.mutateAsync({
        id: milestone.id,
        invoiceFileName: attachPdfFileData.name,
        invoiceFileData: attachPdfFileData.data,
        invoiceFileMimeType: attachPdfFileData.mime,
      });
      toast.success("Factura PDF adjuntada correctamente");
      setPdfOpen(false);
      onMutated?.(milestone.id);
    } catch (err: unknown) {
      setDialogError(errorMessage(err, "Error al adjuntar el PDF."));
    }
  }

  const detailData = getByIdQuery.data ?? milestone;
  const currentInvoicePdfUrl = detailData.invoicePdfUrl ?? milestone.invoicePdfUrl;
  const isConfirmingPayment = paymentMethod === "confirming";
  const confirmingCommission = bankCommissionAmount === "" ? 0 : parseSettlementMoney(bankCommissionAmount);
  const confirmingInterest = bankInterestAmount === "" ? 0 : parseSettlementMoney(bankInterestAmount);
  const confirmingGross = parseSettlementMoney(paymentAmount);
  const confirmingNet =
    confirmingGross !== null && confirmingCommission !== null && confirmingInterest !== null
      ? confirmingGross - confirmingCommission - confirmingInterest
      : null;

  // BILL-04: the displayed confirm actions stay disabled until the existing
  // field validation passes AND the explicit confirmation control is checked.
  const invoiceBaseNumber = parseSettlementMoney(invoiceTaxableBase);
  const invoiceTaxNumber = parseSettlementMoney(invoiceTaxAmount);
  const invoiceWithholdingNumber = parseSettlementMoney(invoiceWithholdingAmount);
  const invoiceTotalNumber = invoiceBaseNumber !== null && invoiceTaxNumber !== null && invoiceWithholdingNumber !== null
    ? invoiceBaseNumber + invoiceTaxNumber - invoiceWithholdingNumber
    : null;
  const invoiceTotalDisplay = invoiceTotalNumber !== null && invoiceTotalNumber > 0
    ? invoiceTotalNumber.toFixed(2)
    : null;
  const invoiceFieldsValid =
    invoiceNumber.trim().length > 0 &&
    invoiceDate.trim().length > 0 &&
    parseSettlementMoney(invoiceTaxableBase) != null &&
    (parseSettlementMoney(invoiceTaxableBase) ?? 0) > 0 &&
    parseSettlementMoney(invoiceTaxAmount) != null &&
    (parseSettlementMoney(invoiceTaxAmount) ?? -1) >= 0 &&
    parseSettlementMoney(invoiceWithholdingAmount) != null &&
    (parseSettlementMoney(invoiceWithholdingAmount) ?? -1) >= 0;

  const rawPaymentAmount = parseSettlementMoney(paymentAmount);
  const paymentAmountValid =
    rawPaymentAmount !== null && Number.isFinite(rawPaymentAmount) && rawPaymentAmount > 0;
  const paymentFeesValid =
    !isConfirmingPayment ||
    (confirmingCommission !== null &&
      confirmingInterest !== null &&
      confirmingCommission >= 0 &&
      confirmingInterest >= 0 &&
      confirmingGross !== null &&
      confirmingCommission + confirmingInterest <= confirmingGross);
  const paymentBalanceBefore = detailData.balanceAmount != null
    ? Number(detailData.balanceAmount)
    : detailData.invoiceAmount == null ? null : Number(detailData.invoiceAmount);
  const canShowRecordCollection = canFinance
    && ["invoiced", "sent_to_client", "pending_collection", "collected"].includes(milestone.status)
    && Boolean(milestone.invoiceNumber?.trim())
    && dateDisplay(milestone.invoiceDate) !== "—"
    && milestone.invoiceFiscalVerified === true
    && milestone.balanceAmount != null
    && (parseSettlementMoney(String(milestone.balanceAmount)) ?? 0) > 0;
  const paymentFieldsValid = paymentDate.trim().length > 0
    && paymentAmountValid
    && paymentFeesValid
    && detailData.invoiceFiscalVerified === true
    && paymentBalanceBefore !== null
    && paymentBalanceBefore > 0
    && rawPaymentAmount !== null
    && rawPaymentAmount <= paymentBalanceBefore + 0.005;
  const paymentBalanceAfterOperation = paymentBalanceBefore == null || rawPaymentAmount === null || !Number.isFinite(rawPaymentAmount)
    ? null
    : paymentBalanceBefore - rawPaymentAmount;

  const quickActionMeta = quickAction ? QUICK_LIFECYCLE_ACTIONS[quickAction] : null;
  const quickActionPending =
    markReady.isPending || notifyAccounting.isPending || markPendingCollection.isPending;

  if (!canReadLifecycle) return null;

  return (
    <>
      <div className="flex items-center gap-1">
        {/* Quick action buttons according to current status & permissions */}
        {!isHistorical && (
          <>
            {milestone.status === "pending" && canOperational && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => openQuickActionDialog("markReady")}
              >
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                Marcar listo para facturar
              </Button>
            )}

            {milestone.status === "ready_to_invoice" && canOperational && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => openQuickActionDialog("notifyAccounting")}
              >
                <Clock className="mr-1 h-3.5 w-3.5" />
                Avisar a Contabilidad
              </Button>
            )}

            {milestone.status === "accounting_notified" && canFinance && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={openInvoiceDialog}
              >
                <FileText className="mr-1 h-3.5 w-3.5" />
                Marcar facturado
              </Button>
            )}

            {milestone.status === "invoiced" && canFinance && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => openQuickActionDialog("markSentToClient")}
              >
                <Clock className="mr-1 h-3.5 w-3.5" />
                Registrar envío al cliente
              </Button>
            )}

            {milestone.status === "sent_to_client" && canFinance && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => openQuickActionDialog("markPendingCollection")}
              >
                <Clock className="mr-1 h-3.5 w-3.5" />
                Marcar pendiente de cobro
              </Button>
            )}

            {milestone.status === "pending_collection" && canFinance && milestone.invoiceNumber && milestone.invoiceFiscalVerified !== true ? (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openInvoiceDialog}>
                <FileText className="mr-1 h-3.5 w-3.5" />
                Completar desglose fiscal
              </Button>
            ) : null}
            {canShowRecordCollection && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openPaymentDialog}>
                  <DollarSign className="mr-1 h-3.5 w-3.5" />
                  Registrar cobro
                </Button>
              </>
            )}
            {milestone.status === "collected" && canFinance && milestone.invoiceNumber && milestone.invoiceFiscalVerified !== true ? (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openInvoiceDialog}>
                <FileText className="mr-1 h-3.5 w-3.5" />
                Completar desglose fiscal
              </Button>
            ) : null}
          </>
        )}

        {/* Dropdown Menu for all actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Acciones de facturación">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={() => setDetailOpen(true)}>
              <Info className="mr-2 h-4 w-4" />
              Ver datos de facturación
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setAuditOpen(true)}>
              <History className="mr-2 h-4 w-4" />
              Ver historial
            </DropdownMenuItem>

            {!isHistorical && (
              <>
                {milestone.status === "pending" && (
                  <>
                    {onEdit && canOperational && (
                      <DropdownMenuItem onSelect={onEdit}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar hito
                      </DropdownMenuItem>
                    )}
                    {canOperational && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={openCancelDialog}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Cancelar hito
                        </DropdownMenuItem>
                      </>
                    )}
                  </>
                )}

                {milestone.status === "ready_to_invoice" && (
                  <>
                    <DropdownMenuSeparator />
                    {canOperational && (
                      <DropdownMenuItem onSelect={() => openQuickActionDialog("notifyAccounting")}>
                        <Clock className="mr-2 h-4 w-4" />
                        Avisar a Contabilidad
                      </DropdownMenuItem>
                    )}
                    {canFinance && (
                      <DropdownMenuItem onSelect={openInvoiceDialog}>
                        <FileText className="mr-2 h-4 w-4" />
                        Registrar factura
                      </DropdownMenuItem>
                    )}
                    {canOperational && (
                      <>
                        <DropdownMenuItem onSelect={openRevertDialog}>
                          <ArrowLeftRight className="mr-2 h-4 w-4" />
                          Revertir a pendiente
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={openCancelDialog}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Cancelar hito
                        </DropdownMenuItem>
                      </>
                    )}
                  </>
                )}

                {milestone.status === "accounting_notified" && (
                  <>
                    <DropdownMenuSeparator />
                    {canFinance && (
                      <DropdownMenuItem onSelect={openInvoiceDialog}>
                        <FileText className="mr-2 h-4 w-4" />
                        Registrar factura
                      </DropdownMenuItem>
                    )}
                    {canOperational && (
                      <>
                        <DropdownMenuItem onSelect={openRevertDialog}>
                          <ArrowLeftRight className="mr-2 h-4 w-4" />
                          Revertir a listo para facturar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={openCancelDialog}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Cancelar hito
                        </DropdownMenuItem>
                      </>
                    )}
                  </>
                )}

                {milestone.status === "invoiced" && (
                  <>
                    <DropdownMenuSeparator />
                    {canFinance && (
                      <>
                        <DropdownMenuItem onSelect={() => openQuickActionDialog("markSentToClient")}>
                          <Clock className="mr-2 h-4 w-4" />
                          Registrar envío al cliente
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={openPdfDialog}>
                          <Upload className="mr-2 h-4 w-4" />
                          Adjuntar/Reemplazar PDF
                        </DropdownMenuItem>
                      </>
                    )}
                    {canOperational && (
                      <DropdownMenuItem onSelect={openRevertDialog}>
                        <ArrowLeftRight className="mr-2 h-4 w-4" />
                        Revertir estado
                      </DropdownMenuItem>
                    )}
                  </>
                )}

                {milestone.status === "sent_to_client" && canFinance && (
                  <DropdownMenuItem onSelect={() => openQuickActionDialog("markPendingCollection")}>
                    <Clock className="mr-2 h-4 w-4" />
                    Marcar pendiente de cobro
                  </DropdownMenuItem>
                )}

                {milestone.status === "pending_collection" && (
                  <>
                    <DropdownMenuSeparator />
                    {canFinance && (
                      <>
                        {milestone.invoiceNumber && milestone.invoiceFiscalVerified !== true ? (
                          <DropdownMenuItem onSelect={openInvoiceDialog}>
                            <FileText className="mr-2 h-4 w-4" />
                            Completar desglose fiscal
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem onSelect={openPdfDialog}>
                          <Upload className="mr-2 h-4 w-4" />
                          Adjuntar/Reemplazar PDF
                        </DropdownMenuItem>
                      </>
                    )}
                    {canOperational && (
                      <DropdownMenuItem onSelect={openRevertDialog}>
                        <ArrowLeftRight className="mr-2 h-4 w-4" />
                        Revertir a enviado al cliente
                      </DropdownMenuItem>
                    )}
                  </>
                )}

                {milestone.status === "collected" && canFinance && (
                  <>
                    <DropdownMenuSeparator />
                    {milestone.invoiceNumber && milestone.invoiceFiscalVerified !== true ? (
                      <DropdownMenuItem onSelect={openInvoiceDialog}>
                        <FileText className="mr-2 h-4 w-4" />
                        Completar desglose fiscal
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem onSelect={openPdfDialog}>
                      <Upload className="mr-2 h-4 w-4" />
                      Adjuntar/Reemplazar PDF
                    </DropdownMenuItem>
                  </>
                )}
                {canShowRecordCollection && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={openPaymentDialog}>
                      <DollarSign className="mr-2 h-4 w-4" />
                      Registrar cobro
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* BILL-04: Quick lifecycle action confirmation dialog. Opening or
          cancelling it never mutates; the mutation runs only after the
          explicit confirmation control is checked and confirmed. */}
      <Dialog
        open={quickAction !== null}
        onOpenChange={(open) => {
          if (!open) setQuickAction(null);
        }}
      >
        <DialogContent className={lifecycleDialogClassName}>
          <DialogHeader className="min-w-0 shrink-0 pr-6">
            <DialogTitle>{quickActionMeta?.title ?? "Confirmar cambio de estado"}</DialogTitle>
            <DialogDescription>{quickActionMeta?.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {dialogError && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{dialogError}</span>
              </div>
            )}

            {quickAction && quickActionMeta && (
              <MilestoneActionSummary
                milestone={milestone}
                currency={currency}
                contextLabel={contextLabel}
                destinationLabel={quickActionMeta.destinationLabel}
              />
            )}

            <div className="flex items-center space-x-2 pt-2">
              <Switch
                id="quick-action-confirm"
                checked={quickActionConfirmed}
                onCheckedChange={setQuickActionConfirmed}
              />
              <Label htmlFor="quick-action-confirm" className="text-xs">
                Confirmo que deseo aplicar este cambio de estado
              </Label>
            </div>
          </div>

          <DialogFooter className="min-w-0 shrink-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:h-auto [&>button]:min-h-9 [&>button]:whitespace-normal">
            <Button variant="outline" onClick={() => setQuickAction(null)}>
              Cancelar
            </Button>
            <Button
              disabled={quickActionPending || !quickActionConfirmed}
              onClick={handleQuickActionConfirm}
            >
              {quickActionPending ? "Aplicando..." : quickActionMeta?.confirmLabel ?? "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className={lifecycleDialogClassName}>
          <DialogHeader className="min-w-0 shrink-0 pr-6">
            <DialogTitle>{milestone.invoiceNumber && detailData.invoiceFiscalVerified !== true ? "Completar desglose fiscal" : "Registrar factura"}</DialogTitle>
            <DialogDescription>
              {milestone.invoiceNumber && detailData.invoiceFiscalVerified !== true
                ? "Completa base, IVA y retención de la factura existente. No se cambiarán su número, fecha ni total; si no coincide, requiere revisión autorizada."
                : "Introduce los datos de la factura emitida para este hito."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {detailData.certificationEconomicReviewRequired === true && (
              <div role="alert" data-testid="invalid-certification-economic-snapshot" className="rounded-md bg-destructive/15 p-3 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{detailData.certificationEconomicReviewMessage ?? "El snapshot económico V1 de la certificación requiere revisión; no se muestran importes como aprobados."}</span>
              </div>
            )}
            {dialogError && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{dialogError}</span>
              </div>
            )}

            <MilestoneActionSummary
              milestone={{ ...milestone, certificationEconomicReviewRequired: detailData.certificationEconomicReviewRequired }}
              currency={currency}
              contextLabel={contextLabel}
              destinationLabel="Facturado"
              amountOverride={invoiceFieldsValid ? invoiceTotalDisplay : null}
            />

            <div className="space-y-1.5">
              <Label htmlFor="inv-num">Número de factura *</Label>
              <Input
                id="inv-num"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="FAC-2026-001"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-date">Fecha de factura *</Label>
              <Input
                id="inv-date"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-taxable-base">Base imponible ({currency}) *</Label>
              {milestone.certificationId != null && detailData.certificationEconomicReviewRequired !== true && detailData.approvedTaxableBase != null && <p className="text-xs text-muted-foreground">Certificación V1: base aprobada {moneyDisplay(detailData.approvedTaxableBase,currency)} · IVA {moneyDisplay(detailData.approvedVatAmount,currency)} · retención {moneyDisplay(detailData.approvedWithholdingAmount,currency)}. El total se calcula sobre esta base, no sobre el hito líquido.</p>}
              <Input
                id="inv-taxable-base"
                type="number"
                step="0.01"
                value={invoiceTaxableBase}
                onChange={(e) => handleInvoiceBaseChange(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="inv-vat-rate">Tipo de IVA</Label>
                <BillingVatRateSelect id="inv-vat-rate" value={invoiceVatRate} onValueChange={handleInvoiceVatRateChange} />
                <Label htmlFor="inv-tax">Cuota IVA ({currency}) *</Label>
                <Input id="inv-tax" type="number" min="0" step="0.01" value={invoiceTaxAmount} onChange={(e) => setInvoiceTaxAmount(e.target.value)} placeholder="0.00" />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="inv-withholding">Retención ({currency}) *</Label>
                <Input id="inv-withholding" type="number" min="0" step="0.01" value={invoiceWithholdingAmount} onChange={(e) => setInvoiceWithholdingAmount(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <p className="text-sm font-medium" data-testid="invoice-payable-total">
              Total exigible: {invoiceTotalDisplay ? moneyDisplay(invoiceTotalDisplay, currency) : "—"}
            </p>

            <div className="space-y-1.5">
              <Label htmlFor="inv-pdf">Documento PDF (opcional)</Label>
              <Input
                id="inv-pdf"
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => handleFileRead(e, setInvoiceFileData)}
              />
              {invoiceFileData && (
                <p className="text-xs text-muted-foreground">
                  Archivo seleccionado: {invoiceFileData.name}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Switch
                id="invoice-confirm"
                checked={invoiceConfirmed}
                onCheckedChange={setInvoiceConfirmed}
              />
              <Label htmlFor="invoice-confirm" className="text-xs">
                Confirmo que deseo registrar esta factura
              </Label>
            </div>
          </div>

          <DialogFooter className="min-w-0 shrink-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:h-auto [&>button]:min-h-9 [&>button]:whitespace-normal">
            <Button variant="outline" onClick={() => setInvoiceOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={markInvoiced.isPending || detailData.certificationEconomicReviewRequired === true || !invoiceConfirmed || !invoiceFieldsValid}
              onClick={handleMarkInvoicedSubmit}
            >
              {markInvoiced.isPending ? "Guardando..." : "Guardar factura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment / Cobro Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className={lifecycleDialogClassName}>
          <DialogHeader className="min-w-0 shrink-0 pr-6">
            <DialogTitle>{isConfirmingPayment ? "Registrar cobro Confirming" : "Registrar cobro"}</DialogTitle>
            <DialogDescription>
              {isConfirmingPayment
                ? "El importe satisfecho es el bruto; los costes financieros se descuentan para obtener el neto bancario."
                : "Introduce la fecha e importe del cobro recibido."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {dialogError && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{dialogError}</span>
              </div>
            )}

            <MilestoneActionSummary
              milestone={milestone}
              currency={currency}
              contextLabel={contextLabel}
              destinationLabel="Factura / cobro"
              amountOverride={paymentAmountValid ? paymentAmount : null}
            />
            {detailData.invoiceFiscalVerified !== true ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
                Esta factura no tiene base, IVA y retención verificables. No se puede registrar un cobro hasta revisar esos datos.
              </div>
            ) : null}
            {detailData.invoiceEconomicReviewRequired ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
                {detailData.invoiceEconomicReviewMessage || "La diferencia entre plan e invoice requiere revisión antes de cerrar el hito."}
              </div>
            ) : null}
            {paymentBalanceAfterOperation !== null ? (
              <div className="rounded-md border p-3 text-sm" data-testid="collection-balance-preview">
                <div>Factura exigible: {moneyDisplay(detailData.invoiceAmount, currency)}</div>
                <div>Cobrado antes: {moneyDisplay(detailData.collectedAmount ?? "0.00", currency)}</div>
                <div>Este cobro: {paymentAmount ? moneyDisplay(paymentAmount, currency) : "—"}</div>
                <div>Saldo posterior: {moneyDisplay(paymentBalanceAfterOperation, currency)}</div>
                {paymentBalanceAfterOperation > 0.005
                  ? "Se registrará como cobro parcial y la factura seguirá pendiente."
                  : paymentBalanceAfterOperation < -0.005
                    ? "El cobro supera el importe exigible. Revisa el importe; no se aplicará automáticamente el excedente."
                    : detailData.invoiceEconomicReviewRequired
                      ? "Factura cobrada; el hito seguirá pendiente de revisión económica."
                      : "Este cobro liquida la factura."}
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="pay-method">Método real del cobro *</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger id="pay-method"><SelectValue placeholder="Selecciona el método" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                  <SelectItem value="confirming">Confirming</SelectItem>
                  <SelectItem value="promissory_note">Pagaré</SelectItem>
                  <SelectItem value="cash">Contado</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pay-date">Fecha de cobro *</Label>
              <Input
                id="pay-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pay-amount">
                {isConfirmingPayment ? "Importe satisfecho bruto" : "Importe cobrado"} ({currency}) *
              </Label>
              <Input
                id="pay-amount"
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pay-reference">Referencia bancaria (opcional)</Label>
              <Input id="pay-reference" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} maxLength={255} />
            </div>

            {isConfirmingPayment && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="pay-bank-commission">Comisión bancaria ({currency})</Label>
                  <Input
                    id="pay-bank-commission"
                    type="number"
                    min="0"
                    step="0.01"
                    value={bankCommissionAmount}
                    onChange={(e) => setBankCommissionAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pay-bank-interest">Intereses ({currency})</Label>
                  <Input
                    id="pay-bank-interest"
                    type="number"
                    min="0"
                    step="0.01"
                    value={bankInterestAmount}
                    onChange={(e) => setBankInterestAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="rounded-md border p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Coste financiero:</span>
                    <span>{confirmingCommission !== null && confirmingInterest !== null
                      ? moneyDisplay(confirmingCommission + confirmingInterest, currency)
                      : "—"}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span className="text-muted-foreground">Neto recibido banco:</span>
                    <span>{confirmingNet !== null ? moneyDisplay(confirmingNet, currency) : "—"}</span>
                  </div>
                </div>
              </>
            )}

            <div className="flex items-center space-x-2 pt-2">
              <Switch
                id="payment-confirm"
                checked={paymentConfirmed}
                onCheckedChange={setPaymentConfirmed}
              />
              <Label htmlFor="payment-confirm" className="text-xs">
                Confirmo que deseo registrar este cobro
              </Label>
            </div>
          </div>

          <DialogFooter className="min-w-0 shrink-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:h-auto [&>button]:min-h-9 [&>button]:whitespace-normal">
            <Button variant="outline" onClick={() => setPaymentOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={recordCollection.isPending || !paymentConfirmed || !paymentFieldsValid}
              onClick={handleMarkCollectedSubmit}
            >
              {recordCollection.isPending ? "Guardando..." : paymentBalanceAfterOperation != null && paymentBalanceAfterOperation > 0.005 ? "Registrar cobro parcial" : "Registrar cobro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className={lifecycleDialogClassName}>
          <DialogHeader className="min-w-0 shrink-0 pr-6">
            <DialogTitle>Cancelar hito de facturación</DialogTitle>
            <DialogDescription>
              Esta acción marcará el hito como cancelado sin eliminar su registro histórico.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {milestone.status === "accounting_notified" && (
              <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                <span>
                  Atención: Notificarás a Contabilidad la cancelación de este hito ya comunicado.
                </span>
              </div>
            )}

            {dialogError && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{dialogError}</span>
              </div>
            )}

            <MilestoneActionSummary
              milestone={milestone}
              currency={currency}
              contextLabel={contextLabel}
              destinationLabel="Cancelado"
            />

            <div className="space-y-1.5">
              <Label htmlFor="cancel-reason">Motivo de cancelación * (5 a 500 caracteres)</Label>
              <Textarea
                id="cancel-reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Explica detalladamente la causa de la cancelación..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground text-right">
                {cancellationReason.trim().length} / 500
              </p>
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Switch
                id="cancel-confirm"
                checked={cancelConfirmed}
                onCheckedChange={setCancelConfirmed}
              />
              <Label htmlFor="cancel-confirm" className="text-xs">
                Confirmo que deseo cancelar este hito de facturación
              </Label>
            </div>
          </div>

          <DialogFooter className="min-w-0 shrink-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:h-auto [&>button]:min-h-9 [&>button]:whitespace-normal">
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={cancelMilestone.isPending || !cancelConfirmed}
              onClick={handleCancelSubmit}
            >
              {cancelMilestone.isPending ? "Cancelando..." : "Confirmar cancelación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert Dialog */}
      <Dialog open={revertOpen} onOpenChange={setRevertOpen}>
        <DialogContent className={lifecycleDialogClassName}>
          <DialogHeader className="min-w-0 shrink-0 pr-6">
            <DialogTitle>Revertir estado de facturación</DialogTitle>
            <DialogDescription>
              Selecciona el estado de destino e indica el motivo de la reversión.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {dialogError && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{dialogError}</span>
              </div>
            )}

            <MilestoneActionSummary
              milestone={milestone}
              currency={currency}
              contextLabel={contextLabel}
              destinationLabel={targetStatus ? REVERT_TARGET_LABELS[targetStatus] : "—"}
            />

            <div className="space-y-1.5">
              <Label htmlFor="revert-target">Estado destino *</Label>
              <Select
                value={targetStatus}
                onValueChange={(value) => setTargetStatus(value as RevertTargetStatus)}
              >
                <SelectTrigger id="revert-target" className="w-full min-w-0 h-auto! min-h-9 whitespace-normal [&_[data-slot=select-value]]:whitespace-normal [&_[data-slot=select-value]]:line-clamp-none">
                  <SelectValue placeholder="Selecciona estado" />
                </SelectTrigger>
                <SelectContent>
                  {milestone.status === "ready_to_invoice" && (
                    <SelectItem value="pending">Pendiente</SelectItem>
                  )}
                  {milestone.status === "accounting_notified" && (
                    <SelectItem value="ready_to_invoice">Listo para facturar</SelectItem>
                  )}
                  {milestone.status === "invoiced" && (
                    <>
                      <SelectItem value="accounting_notified">Contabilidad notificada</SelectItem>
                      <SelectItem value="ready_to_invoice">Listo para facturar</SelectItem>
                    </>
                  )}
                  {milestone.status === "pending_collection" && (
                    <SelectItem value="sent_to_client">Enviado al cliente</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="revert-reason">Motivo de la reversión * (5 a 500 caracteres)</Label>
              <Textarea
                id="revert-reason"
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                placeholder="Motivo por el que se retrocede el estado..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground text-right">
                {revertReason.trim().length} / 500
              </p>
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Switch
                id="revert-confirm"
                checked={revertConfirmed}
                onCheckedChange={setRevertConfirmed}
              />
              <Label htmlFor="revert-confirm" className="text-xs">
                Confirmo que deseo revertir el estado de este hito
              </Label>
            </div>
          </div>

          <DialogFooter className="min-w-0 shrink-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:h-auto [&>button]:min-h-9 [&>button]:whitespace-normal">
            <Button variant="outline" onClick={() => setRevertOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={revertStatus.isPending || !revertConfirmed}
              onClick={handleRevertSubmit}
            >
              {revertStatus.isPending ? "Revertiendo..." : "Confirmar reversión"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Attachment Dialog */}
      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className={lifecycleDialogClassName}>
          <DialogHeader className="min-w-0 shrink-0 pr-6">
            <DialogTitle>Adjuntar PDF de factura</DialogTitle>
            <DialogDescription>
              Selecciona el archivo PDF correspondiente a la factura de este hito.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {dialogError && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{dialogError}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="attach-pdf">Archivo PDF *</Label>
              <Input
                id="attach-pdf"
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => handleFileRead(e, setAttachPdfFileData)}
              />
              {attachPdfFileData && (
                <p className="text-xs text-muted-foreground">
                  Archivo listo: {attachPdfFileData.name}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="min-w-0 shrink-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:h-auto [&>button]:min-h-9 [&>button]:whitespace-normal">
            <Button variant="outline" onClick={() => setPdfOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={attachInvoicePdf.isPending || !attachPdfFileData}
              onClick={handleAttachPdfSubmit}
            >
              {attachInvoicePdf.isPending ? "Subiendo..." : "Subir PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details & Invoicing Data Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className={lifecycleDialogClassName}>
          <DialogHeader className="min-w-0 shrink-0 pr-6">
            <DialogTitle>Datos de facturación e hito</DialogTitle>
            <DialogDescription>
              Información registrada para el hito: {milestone.label}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm py-2">
            <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
              <span className="text-muted-foreground">Concepto:</span>
              <span className="font-medium">{milestone.label}</span>
            </div>

            <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
              <span className="text-muted-foreground">Estado actual:</span>
              <Badge variant="outline">{BILLING_STATUS_LABELS[milestone.status] ?? "Estado registrado"}</Badge>
            </div>

            <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
              <span className="text-muted-foreground">Fecha prevista / vencimiento:</span>
              <span>{dateDisplay(detailData.dueDate)}</span>
            </div>

            <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
              <span className="text-muted-foreground">Número de factura:</span>
              <span className="font-mono">{detailData.invoiceNumber ?? "—"}</span>
            </div>

            <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
              <span className="text-muted-foreground">Fecha de factura:</span>
              <span>{dateDisplay(detailData.invoiceDate)}</span>
            </div>

            <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
              <span className="text-muted-foreground">Importe facturado:</span>
              <span>{moneyDisplay(detailData.invoiceAmount, currency)}</span>
            </div>

            <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
              <span className="text-muted-foreground">Fecha de cobro:</span>
              <span>{dateDisplay(detailData.paymentDate)}</span>
            </div>

            <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
              <span className="text-muted-foreground">Importe cobrado:</span>
              <span>{moneyDisplay(detailData.paymentAmount, currency)}</span>
            </div>

            {detailData.bankCommissionAmount && detailData.bankCommissionAmount !== "0.00" && (
              <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
                <span className="text-muted-foreground">Comisión bancaria:</span>
                <span>{moneyDisplay(detailData.bankCommissionAmount, currency)}</span>
              </div>
            )}
            {detailData.bankInterestAmount && detailData.bankInterestAmount !== "0.00" && (
              <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
                <span className="text-muted-foreground">Intereses bancarios:</span>
                <span>{moneyDisplay(detailData.bankInterestAmount, currency)}</span>
              </div>
            )}
            {detailData.financialCostAmount && (
              <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
                <span className="text-muted-foreground">Coste financiero total:</span>
                <span>{moneyDisplay(detailData.financialCostAmount, currency)}</span>
              </div>
            )}
            {detailData.netReceivedAmount && (
              <div className={`${lifecycleSummaryRowClassName} border-b pb-1.5`}>
                <span className="text-muted-foreground">Neto recibido banco:</span>
                <span>{moneyDisplay(detailData.netReceivedAmount, currency)}</span>
              </div>
            )}

            {currentInvoicePdfUrl && (
              <div className={`${lifecycleSummaryRowClassName} pt-2`}>
                <span className="text-muted-foreground">Documento factura:</span>
                <a
                  href={currentInvoicePdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ods-navigation hover:underline font-medium flex items-center gap-1 text-sm"
                >
                  <FileText className="h-4 w-4" />
                  Ver factura PDF
                </a>
              </div>
            )}
          </div>

          <DialogFooter className="min-w-0 shrink-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:h-auto [&>button]:min-h-9 [&>button]:whitespace-normal">
            <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className={`${lifecycleDialogClassName} sm:max-w-lg`}>
          <DialogHeader className="min-w-0 shrink-0 pr-6">
            <DialogTitle>Historial de auditoría del hito</DialogTitle>
            <DialogDescription>
              Registro cronológico de cambios de estado y acciones operativas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {auditLogQuery.isLoading ? (
              <p className="text-xs text-muted-foreground text-center py-4">Cargando historial...</p>
            ) : (auditLogQuery.data ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                No hay registros de auditoría para este hito.
              </p>
            ) : (
              (auditLogQuery.data ?? []).map((log: BillingMilestoneAuditLog) => {
                const actionLabel = AUDIT_ACTION_LABELS[log.action] ?? "Acción registrada";
                const formattedDate = new Date(log.createdAt).toLocaleString("es-ES");
                const parsedDetails = parseAuditDetails(log.details);

                return (
                  <div key={log.id} className="rounded-md border p-3 text-xs space-y-1 bg-muted/30">
                    <div className="flex flex-wrap items-center justify-between gap-2 font-medium">
                      <span className="font-semibold text-foreground">{actionLabel}</span>
                      <span className="text-muted-foreground">{formattedDate}</span>
                    </div>

                    <p className="text-muted-foreground">Realizado por: {formatBillingAuditActor(log)}</p>

                    {parsedDetails && typeof parsedDetails === "object" && (
                      <div className="pt-1 text-muted-foreground space-y-0.5 border-t mt-1.5">
                        {formatRetentionAuditDetails(log.action, parsedDetails).map(line => (
                          <p key={line}>{line}</p>
                        ))}
                        {typeof parsedDetails.reason === "string" && (
                          <p><strong>Motivo:</strong> {parsedDetails.reason}</p>
                        )}
                        {typeof parsedDetails.cancellationReason === "string" && (
                          <p><strong>Motivo cancelación:</strong> {parsedDetails.cancellationReason}</p>
                        )}
                        {typeof parsedDetails.fromStatus === "string" && typeof parsedDetails.toStatus === "string" && (
                          <p><strong>Estado:</strong> {BILLING_STATUS_LABELS[parsedDetails.fromStatus] ?? "Estado registrado"} → {BILLING_STATUS_LABELS[parsedDetails.toStatus] ?? "Estado registrado"}</p>
                        )}
                        {typeof parsedDetails.invoiceNumber === "string" && (
                          <p><strong>Nº Factura:</strong> {parsedDetails.invoiceNumber}</p>
                        )}
                        {typeof parsedDetails.invoiceAmount === "string" && (
                          <p><strong>Importe factura:</strong> {moneyDisplay(parsedDetails.invoiceAmount, currency)}</p>
                        )}
                        {typeof parsedDetails.paymentAmount === "string" && (
                          <p><strong>Importe cobro:</strong> {moneyDisplay(parsedDetails.paymentAmount, currency)}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter className="min-w-0 shrink-0 [&>button]:min-w-0 [&>button]:shrink [&>button]:h-auto [&>button]:min-h-9 [&>button]:whitespace-normal">
            <Button variant="outline" onClick={() => setAuditOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
